import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Cookie, Shield, X } from 'lucide-react';

const GDPRConsent = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const onConsentChangeRef = useRef(onConsentChange);
  onConsentChangeRef.current = onConsentChange;

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem('gdpr_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      onConsentChangeRef.current(JSON.parse(consent));
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('gdpr_consent', JSON.stringify(consent));
    setShowBanner(false);
    onConsentChange(consent);
  };

  const handleAcceptEssential = () => {
    const consent = {
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('gdpr_consent', JSON.stringify(consent));
    setShowBanner(false);
    onConsentChange(consent);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  const handleSaveCustom = (analytics, marketing) => {
    const consent = {
      analytics,
      marketing,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('gdpr_consent', JSON.stringify(consent));
    setShowBanner(false);
    setShowDetails(false);
    onConsentChange(consent);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* GDPR Consent Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg">
        <div className="container mx-auto max-w-6xl">
          {!showDetails ? (
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start space-x-3">
                <Cookie size={24} className="text-amber-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-text-primary mb-1">
                    We value your privacy
                  </h3>
                  <p className="text-sm text-text-secondary">
                    We use cookies and similar technologies to enhance your browsing experience, 
                    analyze website traffic, and provide personalized content. By clicking "Accept All", 
                    you consent to our use of cookies.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 min-w-fit">
                <Button
                  onClick={handleAcceptEssential}
                  className="btn-base btn-secondary btn-sm"
                >
                  Essential Only
                </Button>
                <Button
                  onClick={handleCustomize}
                  className="btn-base btn-ghost btn-sm"
                >
                  Customize
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="btn-base btn-primary btn-sm"
                >
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            <ConsentDetails onSave={handleSaveCustom} onClose={() => setShowDetails(false)} />
          )}
        </div>
      </div>
    </>
  );
};

const ConsentDetails = ({ onSave, onClose }) => {
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  return (
    <Card className="glass-card p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Shield size={24} className="text-blue-600" />
          <h3 className="heading-5 text-text-primary">Privacy Preferences</h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4 mb-6">
        <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <h4 className="font-semibold text-text-primary">Essential Cookies</h4>
            <p className="text-sm text-text-secondary mt-1">
              Required for basic website functionality. Cannot be disabled.
            </p>
          </div>
          <div className="ml-4">
            <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </div>
          </div>
        </div>

        <div className="flex items-start justify-between p-3 border border-gray-200 rounded-lg">
          <div className="flex-1">
            <h4 className="font-semibold text-text-primary">Analytics Cookies</h4>
            <p className="text-sm text-text-secondary mt-1">
              Help us understand how visitors interact with our website by collecting and reporting information anonymously.
            </p>
          </div>
          <div className="ml-4">
            <button
              onClick={() => setAnalytics(!analytics)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                analytics ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
          </div>
        </div>

        <div className="flex items-start justify-between p-3 border border-gray-200 rounded-lg">
          <div className="flex-1">
            <h4 className="font-semibold text-text-primary">Marketing Cookies</h4>
            <p className="text-sm text-text-secondary mt-1">
              Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
            </p>
          </div>
          <div className="ml-4">
            <button
              onClick={() => setMarketing(!marketing)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                marketing ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={() => onSave(analytics, marketing)}
          className="btn-base btn-primary flex-1"
        >
          Save Preferences
        </Button>
        <Button
          onClick={() => onSave(true, true)}
          className="btn-base btn-secondary"
        >
          Accept All
        </Button>
      </div>
    </Card>
  );
};

export default GDPRConsent;